import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

import { useFindOrders } from '../../../orders/hooks/get-orders.hook';
import {
  OrderTableRow,
  OrderTableWithDetails,
} from '../../../orders/components/Tables/OrderTableWithDetails.component';
import { mapOrderToTableRow } from '../../../orders/utils/map-order-to-table-row.util';
import { ORDER_TABLE_HEADERS } from '../../../orders/constants/order-table-headers.constant';
import { ORDER_TABLE_ROWS_PER_PAGE_OPTIONS } from '../../../orders/constants/order-table-pagination.constants';
import { Order } from '../../../orders/types/order.type';
import { ADMIN_USERS_APP_PATHS } from '../../../users/constants/users-paths.constants';

type Props = {
  invitationId: string;
};

const useStyles = makeStyles((theme) => ({
  link: {
    color: theme.palette.admin.primary.main,
    textDecoration: 'none',

    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const toInvitationOrderRow = (order: Order, linkClassName: string): OrderTableRow => {
  const row = mapOrderToTableRow(order);

  return {
    ...row,
    cells: row.cells.map((cell) =>
      cell.key === 'owner'
        ? {
            ...cell,
            content: (
              <Link className={linkClassName} to={ADMIN_USERS_APP_PATHS.USER_DETAILS(order.owner.id)}>
                {cell.content}
              </Link>
            ),
          }
        : cell
    ),
  };
};

export function InvitationOrdersSection({ invitationId }: Props) {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ORDER_TABLE_ROWS_PER_PAGE_OPTIONS[0]);

  const { loading, orders, totalCount } = useFindOrders({
    page: page + 1,
    limit: rowsPerPage,
    sortDirection: 'desc',
    filters: { invitationId },
  });

  const tableRows = useMemo(
    () => orders.map((order) => toInvitationOrderRow(order, classes.link)),
    [orders, classes.link]
  );

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <OrderTableWithDetails
      maxHeight="60svh"
      isLoading={loading}
      headers={ORDER_TABLE_HEADERS}
      orders={tableRows}
      totalCount={totalCount}
      page={page}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={ORDER_TABLE_ROWS_PER_PAGE_OPTIONS}
      onPageChange={setPage}
      onRowsPerPageChange={handleRowsPerPageChange}
    />
  );
}
